import { useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Paper,
  ClickAwayListener,
} from "@mui/material";

import mainStyle from "./mainStyle.module.scss";

import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up.svg";
import { ReactComponent as ChevronUpHover } from "assets/images/icons/icn_pagination_chevron_up_hover.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down.svg";
import { ReactComponent as ChevronDownHover } from "assets/images/icons/icn_pagination_chevron_down_hover.svg";
import { ReactComponent as Search } from "assets/images/icons/icn_search.svg";
import { ReactComponent as SearchHover } from "assets/images/icons/icn_search_hover.svg";

export const StyledSelect = ({
  value,
  onChange,
  options = [],
  disabledOptions = [],
  placeholder,
  width,
  borderLeftWidth,
  borderRightWidth,
  borderBottomWidth,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchHover, setSearchHover] = useState(false);
  const [chevronHover, setChevronHover] = useState(false);
  const [menuWidthPx, setMenuWidthPx] = useState(width);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const menuOpen = Boolean(anchorEl);

  const filteredOptions = options.filter(
    (opt) =>
      typeof opt.value === "string" &&
      opt.value.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = (event) => {
    event.stopPropagation();
    triggerRef.current = event.currentTarget;
    setAnchorEl(anchorEl ? null : event.currentTarget);
    if (!anchorEl) setSearch("");
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
    setChevronHover(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  const handleSearchClick = (event) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (containerRef.current) setMenuWidthPx(containerRef.current.offsetWidth);
  }, [width]);

  return (
    <Box
      ref={containerRef}
      onClick={handleOpen}
      onMouseEnter={() => setChevronHover(true)}
      onMouseLeave={() => setChevronHover(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        border: `1px solid ${menuOpen
            ? theme.palette.primary.main
            : chevronHover
              ? theme.palette.primary.mainHover
              : theme.palette.primary.borderGreyPrimary
          }`,
        borderRadius: theme.shape.customBorderRadius[1],
        mr: 2,
        width: width,
        height: 29,
        bgcolor: theme.palette.primary.white,
        position: "relative",
        px: 1,
        cursor: "pointer",
      }}
      tabIndex={0}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          overflow: "hidden",
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: theme.typography.customFontSize[2],
            color: theme.palette.primary.greyPrimary,
            fontWeight: theme.typography.customFontWeight[3],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "Open Sans",
          }}
        >
          {value || (
            <span style={{ color: theme.palette.primary.greyThird }}>
              {placeholder}
            </span>
          )}
        </Typography>
      </Box>

      {/* Chevron */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 32,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
          pointerEvents: "auto",
        }}
      >
        {menuOpen ? (
          chevronHover ? (
            <ChevronUpHover />
          ) : (
            <ChevronUp />
          )
        ) : chevronHover ? (
          <ChevronDownHover />
        ) : (
          <ChevronDown />
        )}
      </Box>

      {/* Paper Menu */}
      {menuOpen && (
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              top: "100%",
              right: 0,
              mt: 0.5,
              maxWidth: menuWidthPx,
              borderRadius: 3,
              boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
              zIndex: theme.zIndex.modal,

              "&::before": {
                content: '""',
                position: "absolute",
                top: -8,
                right: 16,
                width: 0,
                height: 0,
                borderLeft: `${borderLeftWidth} solid transparent`,
                borderRight: `${borderRightWidth} solid transparent`,
                borderBottom: `${borderBottomWidth} solid #ffffff`,
                filter:
                  "drop-shadow(-2px -1px 2px rgba(0, 0, 0, 0.05)) drop-shadow(2px -1px 2px rgba(0, 0, 0, 0.05))",
              },
            }}
          >
            {/* Search Box */}
            <Box
              onMouseEnter={() => setSearchHover(true)}
              onMouseLeave={() => setSearchHover(false)}
              onClick={handleSearchClick}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                bgcolor: theme.palette.primary.white,
                px: "10px",
                pt: "10px",
                pb: "5px",
              }}
            >
              <TextField
                fullWidth
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: theme.palette.primary.white,
                    "& fieldset": {
                      borderColor: theme.palette.primary.borderGreyPrimary,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.mainHover,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "7px 14px",
                      fontSize: theme.typography.customFontSize[2],
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchHover ? <SearchHover /> : <Search />}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Options List */}
            <List
              className={mainStyle["custom-scrollbar-menu"]}
              sx={{
                paddingTop: "5px",
                paddingLeft: "10px",
                paddingRight: "10px",
                boxSizing: "content-box",
                marginRight: "10px",
                maxHeight: "180px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {filteredOptions.map((opt) => (
                <ListItem
                  key={opt.label}
                  button
                  selected={value === opt.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(opt);
                    handleClose();
                  }}
                  disabled={disabledOptions.includes(opt.value)}
                  sx={{
                    color: theme.palette.primary.main,
                    paddingLeft: "10px",
                    margin: "5px 5px 0 0",
                    borderRadius: theme.shape.customBorderRadius[0],
                    height: "40px",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.lightBlue,
                    },
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.primary.subBlue,
                    },
                  }}
                >
                  <ListItemText
                    primary={opt.value}
                    primaryTypographyProps={{
                      style: {
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        fontSize: theme.typography.customFontSize[2],
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
};
